import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import {
  Phone,
  Unplug,
  Loader2,
  ExternalLink,
  Mic,
  PhoneCall,
  MessageSquare,
  Brain,
} from 'lucide-react';

interface VoiceConnectionStatus {
  connected: boolean;
  connection?: {
    voice_org_id: string;
    voice_plan: string;
    status: string;
    provisioned_at: string;
  };
}

interface VoiceSsoResult {
  embed_url: string;
  token: string;
  expires_at: string;
}

// Plans available at activation
const PLANS = [
  { id: 'ai_starter', label: 'AI Starter', price: '$49/user/mo', desc: 'VoIP + AI features, up to 15 users' },
  { id: 'ai_business', label: 'AI Business', price: '$89/user/mo', desc: 'Unlimited users + analytics + API' },
  { id: 'voip_standard', label: 'VoIP Standard', price: '$39/user/mo', desc: 'Traditional VoIP, no AI, up to 25 users' },
  { id: 'voip_pro', label: 'VoIP Pro', price: '$69/user/mo', desc: 'Traditional VoIP with IVR and analytics' },
] as const;

const FEATURE_HIGHLIGHTS = [
  { icon: PhoneCall, title: 'Enterprise VoIP', desc: 'Full PBX: call queues, hunt groups, IVR, call park, transfer, recording, and E911 compliance.' },
  { icon: Brain, title: 'AI Call Intelligence', desc: 'Magic Number caller context, post-call summaries, agent scoring, and daily briefings powered by Azure OpenAI.' },
  { icon: MessageSquare, title: 'SMS & Messaging', desc: '10DLC-compliant SMS, team messaging channels, virtual fax, and video conferencing via Telnyx Video.' },
  { icon: Mic, title: 'AI Conversational IVR', desc: 'Natural-language auto-attendant that understands intent and routes callers without pressing a number.' },
];

export function VoicePage() {
  const qc = useQueryClient();
  const [selectedPlan, setSelectedPlan] = useState<string>('ai_starter');
  const [activateError, setActivateError] = useState('');

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['voice-connection'],
    queryFn: () => api<VoiceConnectionStatus>('voice/connect'),
  });

  // SSO embed URL — only fetched when connected
  const { data: sso, isLoading: ssoLoading, refetch: refetchSso } = useQuery({
    queryKey: ['voice-sso'],
    queryFn: () => api<VoiceSsoResult>('voice/sso'),
    enabled: status?.connected === true,
    // Token lives ~5 min — refresh before it expires
    staleTime: 4 * 60 * 1000,
    refetchInterval: 4 * 60 * 1000,
  });

  const activateMutation = useMutation({
    mutationFn: (plan: string) =>
      api<VoiceConnectionStatus['connection']>('voice/connect', {
        method: 'POST',
        body: { plan },
      }),
    onSuccess: () => {
      setActivateError('');
      qc.invalidateQueries({ queryKey: ['voice-connection'] });
    },
    onError: (err) => {
      setActivateError(err instanceof ApiError ? err.message : 'Activation failed. Please try again.');
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => api('voice/connect', { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['voice-connection'] });
      qc.removeQueries({ queryKey: ['voice-sso'] });
    },
  });

  if (statusLoading) {
    return (
      <div className="p-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const connected = status?.connected ?? false;

  // ── Connected: show Voice admin iframe ──────────────────────────
  if (connected) {
    return (
      <div className="flex flex-col h-full">
        {/* Header bar */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-card flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-orange-500/10 flex items-center justify-center">
              <Phone className="h-4 w-4 text-orange-500" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-foreground">The One Voice</h1>
              <p className="text-xs text-muted-foreground capitalize">
                {status?.connection?.voice_plan?.replace(/_/g, ' ')} · Active
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetchSso()}
              disabled={ssoLoading}
              title="Reload"
              className="p-1.5 rounded hover:bg-accent text-muted-foreground"
            >
              <Loader2 className={`h-3.5 w-3.5 ${ssoLoading ? 'animate-spin' : ''}`} />
            </button>
            <a
              href="https://admin.theonevoice.app"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-md"
            >
              <ExternalLink className="h-3 w-3" />
              Open standalone
            </a>
            <button
              onClick={() => disconnectMutation.mutate()}
              disabled={disconnectMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground hover:text-destructive border border-border rounded-md hover:border-destructive transition-colors"
            >
              <Unplug className="h-3 w-3" />
              Disconnect
            </button>
          </div>
        </div>

        {/* Iframe or loading */}
        <div className="flex-1 relative">
          {ssoLoading || !sso ? (
            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading Voice dashboard…</span>
            </div>
          ) : (
            <iframe
              src={sso.embed_url}
              className="w-full h-full border-0"
              title="The One Voice Admin"
              allow="microphone; camera; autoplay"
            />
          )}
        </div>
      </div>
    );
  }

  // ── Not connected: activation flow ──────────────────────────────
  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <div className="h-10 w-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
          <Phone className="h-5 w-5 text-orange-500" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">The One Voice</h1>
          <p className="text-sm text-muted-foreground">Enterprise UCaaS with AI-powered call intelligence</p>
        </div>
      </div>

      {/* Plan picker */}
      <div className="rounded-lg border border-border bg-card p-6 mb-6">
        <h2 className="font-semibold text-foreground mb-4">Choose a plan</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          {PLANS.map((plan) => (
            <button
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`text-left p-4 rounded-lg border-2 transition-colors ${
                selectedPlan === plan.id
                  ? 'border-orange-500 bg-orange-500/5'
                  : 'border-border hover:border-orange-500/50'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm text-foreground">{plan.label}</span>
                <span className="text-sm font-semibold text-orange-500">{plan.price}</span>
              </div>
              <p className="text-xs text-muted-foreground">{plan.desc}</p>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => activateMutation.mutate(selectedPlan)}
            disabled={activateMutation.isPending}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white rounded-md transition-colors disabled:opacity-50"
          >
            {activateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Phone className="h-4 w-4" />
            )}
            {activateMutation.isPending ? 'Activating…' : 'Activate Voice'}
          </button>
          <p className="text-xs text-muted-foreground">
            Billing is managed directly by The One Voice via Stripe.
          </p>
        </div>

        {activateError && (
          <p className="mt-3 text-sm text-destructive">{activateError}</p>
        )}
      </div>

      {/* Feature highlights */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {FEATURE_HIGHLIGHTS.map((f) => (
          <div key={f.title} className="rounded-lg border border-border bg-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <f.icon className="h-4 w-4 text-orange-500" />
              <h3 className="font-semibold text-sm text-foreground">{f.title}</h3>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
